import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';

import { getAuthHeader } from '@/utils/request';

import type { AiAnalysisFilePayload } from '../../utils/aiAnalysisFiles';
import { streamMock } from './useAiAnalysisStream.mock';

const USE_MOCK = false;

type StreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export type AiAnalysisTokensUsed = {
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  file_tokens: number;
};

export type AiAnalysisFileId = {
  id: string;
  file_type: string;
};

type StartOptions = {
  focusId: number;
  initialRequest?: string;
  files?: Array<AiAnalysisFilePayload>;
  onChunk?: (chunk: string) => void;
  onComplete?: () => void;
};

type UpdateOptions = {
  focusId: number;
  analysisThreadId: string;
  existingAnalysis: string;
  modificationRequest: string;
  tokensUsed: AiAnalysisTokensUsed;
  fileIds?: Array<AiAnalysisFileId>;
  onChunk?: (chunk: string) => void;
  onComplete?: () => void;
};

const getBaseUrl = () =>
  (process.env.SERVER_BACKEND_URL || process.env.NEXT_PUBLIC_BACKEND_URL || '')
    .toString()
    .replace(/\/$/, '');

const getGenerateUrl = () => `${getBaseUrl()}/api/ai-analysis/generate/`;
const getUpdateUrl = () => `${getBaseUrl()}/api/ai-analysis/update/`;

const parseHttpErrorMessage = (responseText: string, status: number): string => {
  const fallback = `Stream error (${status}).`;

  try {
    const parsed = JSON.parse(responseText) as {
      message?: string;
      error?: string;
      validation_error?: string | { [key: string]: unknown };
      detail?: string;
    };

    if (typeof parsed?.message === 'string' && parsed.message) {
      return parsed.message;
    }

    if (typeof parsed?.detail === 'string' && parsed.detail) {
      return parsed.detail;
    }

    if (typeof parsed?.error === 'string' && parsed.error) {
      return parsed.error;
    }

    if (typeof parsed?.validation_error === 'string' && parsed.validation_error) {
      return parsed.validation_error;
    }

    if (parsed?.validation_error && typeof parsed.validation_error === 'object') {
      return JSON.stringify(parsed.validation_error);
    }
  } catch {}

  return responseText.trim() || fallback;
};

const trySetErrorFromStreamPayload = (
  payload: string,
  setErrorState: (msg: string) => void,
  setStatusState: (s: StreamStatus) => void
): boolean => {
  try {
    const parsed = JSON.parse(payload);

    if (typeof parsed?.error === 'string') {
      const msg =
        typeof parsed.message === 'string' ? `${parsed.error}: ${parsed.message}` : parsed.error;

      setErrorState(msg);
      setStatusState('error');

      return true;
    }
  } catch {}

  return false;
};

export const useAiAnalysisStream = () => {
  const { data: session } = useSession();
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [output, setOutput] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasReceivedChunkRef = useRef(false);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStatus('closed');
  }, []);

  const readStream = useCallback(
    async (
      reader: ReadableStreamDefaultReader<Uint8Array>,
      contentType: string,
      onChunk?: (chunk: string) => void,
      onComplete?: () => void
    ) => {
      const isEventStream = contentType.includes('text/event-stream');
      const decoder = new TextDecoder();
      let buffer = '';
      let streamErrorOccurred = false;

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });

        if (!isEventStream) {
          if (trySetErrorFromStreamPayload(chunk, setError, setStatus)) {
            streamErrorOccurred = true;
            break;
          }
          hasReceivedChunkRef.current = true;
          setOutput(prev => `${prev}${chunk}`);
          onChunk?.(chunk);
          continue;
        }

        buffer += chunk;
        const events = buffer.split('\n\n');

        buffer = events.pop() ?? '';

        for (const eventBlock of events) {
          const lines = eventBlock.split('\n').map(line => line.trim());
          const dataLines = lines
            .filter(line => line.startsWith('data:'))
            .map(line => line.replace(/^data:\s?/, ''));

          if (dataLines.length) {
            const payload = dataLines.join('\n');

            if (trySetErrorFromStreamPayload(payload, setError, setStatus)) {
              streamErrorOccurred = true;
              break;
            }
            hasReceivedChunkRef.current = true;
            setOutput(prev => `${prev}${payload}`);
            onChunk?.(payload);
          } else if (eventBlock.trim()) {
            if (trySetErrorFromStreamPayload(eventBlock, setError, setStatus)) {
              streamErrorOccurred = true;
              break;
            }
            hasReceivedChunkRef.current = true;
            setOutput(prev => `${prev}${eventBlock}`);
            onChunk?.(eventBlock);
          }
        }

        if (streamErrorOccurred) {
          break;
        }
      }

      if (!streamErrorOccurred && isEventStream && buffer.trim()) {
        if (trySetErrorFromStreamPayload(buffer, setError, setStatus)) {
          streamErrorOccurred = true;
        } else {
          hasReceivedChunkRef.current = true;
          setOutput(prev => `${prev}${buffer}`);
          onChunk?.(buffer);
        }
      }

      if (streamErrorOccurred) {
        return;
      }

      setStatus('closed');
      onComplete?.();
    },
    []
  );

  const start = useCallback(
    async ({ focusId, initialRequest, files, onChunk, onComplete }: StartOptions) => {
      if (!session?.accessToken) {
        setError('Missing access token.');
        setStatus('error');

        return;
      }

      if (!focusId) {
        setError('Missing focus_id.');
        setStatus('error');

        return;
      }

      stop();
      setError(null);
      setOutput('');
      hasReceivedChunkRef.current = false;
      setStatus('connecting');

      const controller = new AbortController();

      abortControllerRef.current = controller;

      if (USE_MOCK) {
        setStatus('open');
        await streamMock(onChunk, onComplete, controller.signal, `mock-thread-${Date.now()}`);

        if (!controller.signal.aborted) setStatus('closed');

        return;
      }

      try {
        const body: {
          focus_id: number;
          initial_request?: string;
          files?: Array<AiAnalysisFilePayload>;
        } = { focus_id: focusId };

        if (initialRequest) {
          body.initial_request = initialRequest;
        }

        if (files?.length) {
          body.files = files;
        }

        const response = await fetch(getGenerateUrl(), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream, application/json',
            'Content-Type': 'application/json',
            Authorization: getAuthHeader(session.accessToken),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const responseText = await response.text().catch(() => '');

          setStatus('error');
          setError(parseHttpErrorMessage(responseText, response.status));

          return;
        }

        const reader = response.body?.getReader();
        const contentType = response.headers.get('content-type') || '';

        if (!reader) {
          setStatus('error');
          setError('Streaming is not supported in this browser.');

          return;
        }

        setStatus('open');
        await readStream(reader, contentType, onChunk, onComplete);
      } catch (streamError) {
        if ((streamError as Error).name === 'AbortError') {
          setStatus('closed');

          return;
        }

        if (hasReceivedChunkRef.current) {
          setStatus('closed');
          onComplete?.();
        } else {
          setStatus('error');
          setError('Stream connection failed.');
        }
      }
    },
    [session?.accessToken, stop, readStream]
  );

  const update = useCallback(
    async ({
      focusId,
      analysisThreadId,
      existingAnalysis,
      modificationRequest,
      tokensUsed,
      fileIds,
      onChunk,
      onComplete,
    }: UpdateOptions) => {
      if (!session?.accessToken) {
        setError('Missing access token.');
        setStatus('error');

        return;
      }

      if (!focusId || !analysisThreadId) {
        setError('Missing focus_id or analysis_thread_id.');
        setStatus('error');

        return;
      }

      stop();
      setError(null);
      setOutput('');
      hasReceivedChunkRef.current = false;
      setStatus('connecting');

      const controller = new AbortController();

      abortControllerRef.current = controller;

      if (USE_MOCK) {
        setStatus('open');
        await streamMock(onChunk, onComplete, controller.signal, analysisThreadId);

        if (!controller.signal.aborted) setStatus('closed');

        return;
      }

      try {
        const body: {
          focus_id: number;
          analysis_thread_id: string;
          existing_analysis: string;
          modification_request: string;
          tokens_used: AiAnalysisTokensUsed;
          file_ids?: Array<AiAnalysisFileId>;
        } = {
          focus_id: focusId,
          analysis_thread_id: analysisThreadId,
          existing_analysis: existingAnalysis,
          modification_request: modificationRequest,
          tokens_used: tokensUsed,
        };

        if (fileIds?.length) {
          body.file_ids = fileIds;
        }

        const response = await fetch(getUpdateUrl(), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream, application/json',
            'Content-Type': 'application/json',
            Authorization: getAuthHeader(session.accessToken),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const responseText = await response.text().catch(() => '');

          setStatus('error');
          setError(parseHttpErrorMessage(responseText, response.status));

          return;
        }

        const reader = response.body?.getReader();
        const contentType = response.headers.get('content-type') || '';

        if (!reader) {
          setStatus('error');
          setError('Streaming is not supported in this browser.');

          return;
        }

        setStatus('open');
        await readStream(reader, contentType, onChunk, onComplete);
      } catch (streamError) {
        if ((streamError as Error).name === 'AbortError') {
          setStatus('closed');

          return;
        }

        if (hasReceivedChunkRef.current) {
          setStatus('closed');
          onComplete?.();
        } else {
          setStatus('error');
          setError('Stream connection failed.');
        }
      }
    },
    [session?.accessToken, stop, readStream]
  );

  useEffect(() => () => stop(), [stop]);

  return useMemo(
    () => ({
      status,
      error,
      output,
      start,
      update,
      stop,
    }),
    [status, error, output, start, update, stop]
  );
};
